import * as React from "react";
import { Box } from "@mui/material";
import dayjs from "dayjs";
import { FilterDate, FilterToggle } from "./FilterBar";

export const DEFAULT_RANGES = [
  { value: 7, label: "7d" },
  { value: 30, label: "30d" },
  { value: 90, label: "90d" },
];

export const CUSTOM = "custom";

/**
 * Preset day-ranges plus an explicit from/to range.
 *
 * The dashboards previously offered 7/30/90 only, so any other window — a
 * billing month, a client's quarter — was unreachable.
 *
 * Built on FilterToggle rather than a bare ToggleButtonGroup: grouped buttons
 * flatten their corners and collapse their borders when they wrap to a second
 * line, which is exactly what a four-option group does on a phone. FilterToggle
 * already solves that, and it keeps this consistent with the other filters.
 */
export default function DateRangeFilter({
  value,
  onChange,
  from,
  to,
  onFromChange,
  onToChange,
  ranges = DEFAULT_RANGES,
}) {
  const isCustom = value === CUSTOM;
  const options = [...ranges, { value: CUSTOM, label: "Custom" }];

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 1,
        alignItems: "center",
        width: { xs: "100%", sm: "auto" },
      }}
    >
      <FilterToggle value={value} onChange={onChange} options={options} />

      {isCustom && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            width: { xs: "100%", sm: "auto" },
          }}
        >
          <FilterDate
            label="From"
            value={from}
            onChange={(event) => onFromChange(event.target.value)}
            sx={{ flex: { xs: "1 1 100%", sm: "0 0 auto" }, width: { sm: 180 } }}
          />
          <FilterDate
            label="To"
            value={to}
            onChange={(event) => onToChange(event.target.value)}
            sx={{ flex: { xs: "1 1 100%", sm: "0 0 auto" }, width: { sm: 180 } }}
          />
        </Box>
      )}
    </Box>
  );
}

/** "22 Aug – 20 Sep 2026" — the span the numbers on screen actually cover. */
export function formatRange(startDate, endDate) {
  if (!startDate || !endDate) return "";
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  if (!start.isValid() || !end.isValid()) return "";

  const sameYear = start.isSame(end, "year");
  return `${start.format(sameYear ? "D MMM" : "D MMM YYYY")} – ${end.format("D MMM YYYY")}`;
}
